// frontend/src/components/AtendimentoList.js
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Autocomplete, TextField, Button, Typography, useTheme, useMediaQuery } from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

export default function AtendimentoList({ atendimentos, onEdit, onDelete, canModify, onGenerateReport }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [rows, setRows] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [fAtendente, setFAtendente] = useState(null);
  const [fLoja, setFLoja] = useState(null);
  const [fDe, setFDe] = useState('');
  const [fAte, setFAte] = useState('');

  useEffect(() => {
    if (Array.isArray(atendimentos)) {
      const mappedRows = atendimentos.map(item => ({
        id: item.id,
        atendente: item.atendente,
        dia: item.dia,
        horaInicio: item.hora_inicio,
        horaFim: item.hora_fim,
        loja: item.loja,
        contato: item.contato,
        ocorrencia: item.ocorrencia
      }));
      setRows(mappedRows);
    }
  }, [atendimentos]);

  // opções dos filtros: valores que existem de fato nos atendimentos
  const atendenteOptions = useMemo(
    () => [...new Set(rows.map(r => r.atendente).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [rows]
  );
  const lojaOptions = useMemo(
    () => [...new Set(rows.map(r => r.loja).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [rows]
  );

  // 'dia' vem como YYYY-MM-DD, então comparar como texto funciona
  const filteredRows = useMemo(
    () =>
      rows.filter(r =>
        (!fAtendente || r.atendente === fAtendente) &&
        (!fLoja || r.loja === fLoja) &&
        (!fDe || r.dia >= fDe) &&
        (!fAte || r.dia <= fAte)
      ),
    [rows, fAtendente, fLoja, fDe, fAte]
  );

  const hasFilters = Boolean(fAtendente || fLoja || fDe || fAte);

  // ao mudar o filtro, volta para a primeira página
  useEffect(() => {
    setPaginationModel(m => ({ ...m, page: 0 }));
  }, [fAtendente, fLoja, fDe, fAte]);

  const clearFilters = () => {
    setFAtendente(null);
    setFLoja(null);
    setFDe('');
    setFAte('');
  };

  const columns = [
    { field: 'atendente', headerName: 'Atendente', flex: 1, minWidth: 150 },
    { field: 'dia', headerName: 'Data', flex: 1, minWidth: 120 },
    { field: 'horaInicio', headerName: 'Início', flex: 1, minWidth: 120 },
    { field: 'horaFim', headerName: 'Fim', flex: 1, minWidth: 120 },
    { field: 'loja', headerName: 'Loja', flex: 2, minWidth: 150 },
    { field: 'contato', headerName: 'Contato', flex: 2, minWidth: 150 },
    { field: 'ocorrencia', headerName: 'Ocorrência', flex: 2, minWidth: 150 },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Ações',
      width: 100,
      // só o dono do atendimento (ou um DEV) vê as ações; o backend valida de novo
      getActions: params => {
        if (canModify && !canModify(params.row)) return [];
        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Editar"
            onClick={() => onEdit && onEdit(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Excluir"
            onClick={() => onDelete && onDelete(params.id)}
          />
        ];
      }
    }
  ];

  const columnVisibilityModel = {
    atendente: !isMobile,
    loja: !isTablet,
    contato: !isTablet
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          mb: 1.5
        }}
      >
        <Autocomplete
          size="small"
          options={atendenteOptions}
          value={fAtendente}
          onChange={(_, v) => setFAtendente(v)}
          noOptionsText="Nenhum atendente"
          sx={{ minWidth: { xs: '100%', sm: 200 }, flex: { sm: '1 1 200px' } }}
          renderInput={params => <TextField {...params} label="Atendente" />}
        />
        <Autocomplete
          size="small"
          options={lojaOptions}
          value={fLoja}
          onChange={(_, v) => setFLoja(v)}
          noOptionsText="Nenhuma loja"
          sx={{ minWidth: { xs: '100%', sm: 200 }, flex: { sm: '1 1 200px' } }}
          renderInput={params => <TextField {...params} label="Loja" />}
        />
        <TextField
          size="small"
          type="date"
          label="De"
          value={fDe}
          onChange={e => setFDe(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ flex: { xs: '1 1 130px', sm: '0 0 150px' } }}
        />
        <TextField
          size="small"
          type="date"
          label="Até"
          value={fAte}
          onChange={e => setFAte(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ flex: { xs: '1 1 130px', sm: '0 0 150px' } }}
        />
        {hasFilters && (
          <Button size="small" onClick={clearFilters}>Limpar filtros</Button>
        )}
        {onGenerateReport && (
          <Button
            variant="contained"
            size="small"
            disabled={!hasFilters || filteredRows.length === 0}
            onClick={() =>
              onGenerateReport({ atendente: fAtendente, loja: fLoja, de: fDe, ate: fAte })
            }
            title={hasFilters ? 'Gera o PDF com os atendimentos filtrados' : 'Aplique ao menos um filtro para gerar o relatório'}
          >
            Gerar relatório
          </Button>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
          {hasFilters
            ? `${filteredRows.length} de ${rows.length} atendimentos`
            : `${rows.length} atendimentos`}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: { xs: 'calc(100vh - 340px)', sm: 'calc(100vh - 300px)', md: 'calc(100vh - 240px)' },
          minHeight: 420,
          width: '100%'
        }}
      >
        <DataGrid
          rows={filteredRows}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          pagination
          disableRowSelectionOnClick
          density={isMobile ? 'compact' : 'standard'}
          columnVisibilityModel={columnVisibilityModel}
          localeText={{ noRowsLabel: 'Nenhum atendimento encontrado' }}
          sx={{
            flex: 1,
            border: 'none',
            fontSize: { xs: 13, sm: 14 },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: theme.palette.action.hover,
              borderRadius: 0
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: theme.palette.background.default
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none'
            }
          }}
        />
      </Box>
    </>
  );
}
